import React, { useState, useEffect, useCallback } from 'react';
import { applicants } from '../api';
import ApplicantNotes from './ApplicantNotes';
import ApplicantResume from './ApplicantResume';

function ReleasedList({ user }) {
  const [applicantsList, setApplicantsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState(''); // What user types
  const [activeSearch, setActiveSearch] = useState(''); // What actually gets searched
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [viewingApplicant, setViewingApplicant] = useState(null);
  const [sortField, setSortField] = useState('released_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [filterOptions, setFilterOptions] = useState({ positions: [], locations: [] });
  const [filters, setFilters] = useState({
    position: '',
    location: '',
    minRating: '',
    maxRating: ''
  });
  const [tempFilters, setTempFilters] = useState({
    position: '',
    location: '',
    minRating: '',
    maxRating: ''
  });

  const loadApplicants = useCallback(async () => {
    if (applicantsList.length === 0) {
      setLoading(true);
    }
    try {
      const params = {
        status: 'released',
        page: currentPage,
        limit: itemsPerPage,
        sort_by: sortField,
        sort_order: sortDirection,
        search: activeSearch,
        position_filter: filters.position,
        location_filter: filters.location,
        min_rating: filters.minRating,
        max_rating: filters.maxRating
      };
      
      const response = await applicants.getAll(params);
      setApplicantsList(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error loading applicants:', error);
    } finally {
      setLoading(false);
    }
  }, [activeSearch, filters, sortField, sortDirection, currentPage, itemsPerPage, applicantsList.length]);

  // Load applicants when dependencies change
  useEffect(() => {
    loadApplicants();
  }, [loadApplicants]);

  // Filter dropdown options come from the full data set, not just the current
  // page, so a position that happens to be on page 3 is still selectable.
  useEffect(() => {
    applicants.getFilterOptions('released')
      .then((response) => setFilterOptions(response.data))
      .catch(() => setFilterOptions({ positions: [], locations: [] }));
  }, []);

  const handleSearch = () => {
    setActiveSearch(searchText);
    setCurrentPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleViewDetails = (applicant) => {
    setViewingApplicant(applicant);
    setShowDetailsModal(true);
  };

  const handleExportPDF = async () => {
    try {
      const response = await applicants.download(viewingApplicant.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `application_${viewingApplicant.first_name}_${viewingApplicant.last_name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Error downloading PDF');
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return ' ⇅';
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  const clearFilters = () => {
    setSearchText('');
    setActiveSearch(''); // Clear active search too
    setFilters({
      position: '',
      location: '',
      minRating: '',
      maxRating: ''
    });
  };

  const handleOpenAdvancedFilter = () => {
    setTempFilters({...filters});
    setShowAdvancedFilter(true);
  };

  const handleApplyFilters = () => {
    setFilters({...tempFilters});
    setShowAdvancedFilter(false);
  };

  const handleCancelFilters = () => {
    setShowAdvancedFilter(false);
  };

  const uniquePositions = filterOptions.positions;
  const uniqueLocations = filterOptions.locations;

  // Calculate display values from server pagination
  const startIndex = (pagination.page - 1) * pagination.limit;
  const endIndex = Math.min(startIndex + pagination.limit, pagination.total);

  if (loading) return <div className="spinner"></div>;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Released List</h1>
        <p className="page-subtitle">Applicants not selected for hire</p>
      </div>

      {/* Search and Filter Bar */}
      <div className="card" style={{marginBottom: '20px'}}>
        <div style={{display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap'}}>
          <div style={{display: 'flex', gap: '8px', flex: '1', minWidth: '250px'}}>
            <input
              type="text"
              className="form-input"
              placeholder="Search by name..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              style={{flex: '1'}}
            />
            <button 
              className="btn btn-primary" 
              onClick={handleSearch}
            >
              🔍 Search
            </button>
          </div>
          <button 
            className="btn btn-outline" 
            onClick={handleOpenAdvancedFilter}
          >
            🔧 Advanced Filter
          </button>
          {(activeSearch || filters.position || filters.location || filters.minRating || filters.maxRating) && (
            <button 
              className="btn btn-outline" 
              onClick={clearFilters}
              style={{borderColor: '#dc2626', color: '#dc2626'}}
            >
              ✕ Clear Filters
            </button>
          )}
          <div style={{marginLeft: 'auto', color: '#64748b', fontSize: '14px'}}>
            Showing {startIndex + 1}-{endIndex} of {pagination.total}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th onClick={() => handleSort('first_name')} style={{cursor: 'pointer', userSelect: 'none'}}>
                  Name{getSortIcon('first_name')}
                </th>
                <th onClick={() => handleSort('phone')} style={{cursor: 'pointer', userSelect: 'none'}}>
                  Phone{getSortIcon('phone')}
                </th>
                <th onClick={() => handleSort('position_title')} style={{cursor: 'pointer', userSelect: 'none'}}>
                  Position{getSortIcon('position_title')}
                </th>
                <th onClick={() => handleSort('position_location')} style={{cursor: 'pointer', userSelect: 'none'}}>
                  Location{getSortIcon('position_location')}
                </th>
                <th onClick={() => handleSort('applied_date')} style={{cursor: 'pointer', userSelect: 'none'}}>
                  Applied Date{getSortIcon('applied_date')}
                </th>
                <th onClick={() => handleSort('released_date')} style={{cursor: 'pointer', userSelect: 'none'}}>
                  Released Date{getSortIcon('released_date')}
                </th>
                <th onClick={() => handleSort('rating')} style={{cursor: 'pointer', userSelect: 'none'}}>
                  Rating{getSortIcon('rating')}
                </th>
              </tr>
            </thead>
            <tbody>
              {applicantsList.map(applicant => (
                <tr key={applicant.id} style={{cursor: 'pointer'}} onClick={() => handleViewDetails(applicant)}>
                  <td><strong>{applicant.first_name} {applicant.last_name}</strong></td>
                  <td>{applicant.phone || 'N/A'}</td>
                  <td>{applicant.position_title}</td>
                  <td>{applicant.position_location || 'N/A'}</td>
                  <td>{new Date(applicant.applied_date).toLocaleDateString()}</td>
                  <td>{new Date(applicant.released_date).toLocaleDateString()}</td>
                  <td>{applicant.rating ? `${applicant.rating}/5` : 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div style={{
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            gap: '8px', 
            padding: '20px',
            borderTop: '1px solid #e2e8f0'
          }}>
            <button
              className="btn btn-sm btn-outline"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              style={{opacity: currentPage === 1 ? 0.5 : 1}}
            >
              ⟪ First
            </button>
            <button
              className="btn btn-sm btn-outline"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              style={{opacity: currentPage === 1 ? 0.5 : 1}}
            >
              ← Prev
            </button>
            
            <div style={{display: 'flex', gap: '4px', alignItems: 'center'}}>
              {[...Array(pagination.totalPages)].map((_, idx) => {
                const pageNum = idx + 1;
                if (
                  pageNum === 1 || 
                  pageNum === 2 ||
                  pageNum === pagination.totalPages - 1 ||
                  pageNum === pagination.totalPages ||
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNum}
                      className={currentPage === pageNum ? "btn btn-sm btn-primary" : "btn btn-sm btn-outline"}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (
                  pageNum === currentPage - 2 ||
                  pageNum === currentPage + 2
                ) {
                  return <span key={pageNum}>...</span>;
                }
                return null;
              })}
            </div>

            <button
              className="btn btn-sm btn-outline"
              onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
              disabled={currentPage === pagination.totalPages}
              style={{opacity: currentPage === pagination.totalPages ? 0.5 : 1}}
            >
              Next →
            </button>
            <button
              className="btn btn-sm btn-outline"
              onClick={() => setCurrentPage(pagination.totalPages)}
              disabled={currentPage === pagination.totalPages}
              style={{opacity: currentPage === pagination.totalPages ? 0.5 : 1}}
            >
              Last ⟫
            </button>
          </div>
        )}
      </div>

      {/* Advanced Filter Modal */}
      {showAdvancedFilter && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth: '600px'}}>
            <div className="modal-header">
              <h3 className="modal-title">Advanced Filter</h3>
              <button onClick={handleCancelFilters}>×</button>
            </div>
            <div className="modal-body">
              <div className="grid grid-2" style={{gap: '16px'}}>
                <div className="form-group">
                  <label className="form-label">Position</label>
                  <select
                    className="form-select"
                    value={tempFilters.position}
                    onChange={(e) => setTempFilters({...tempFilters, position: e.target.value})}
                  >
                    <option value="">All Positions</option>
                    {uniquePositions.map(pos => (
                      <option key={pos} value={pos}>{pos}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Location</label>
                  <select
                    className="form-select"
                    value={tempFilters.location}
                    onChange={(e) => setTempFilters({...tempFilters, location: e.target.value})}
                  >
                    <option value="">All Locations</option>
                    {uniqueLocations.map(loc => (
                      <option key={loc} value={loc}>{loc}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Min Rating</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="e.g., 1"
                    min="1"
                    max="5"
                    value={tempFilters.minRating}
                    onChange={(e) => setTempFilters({...tempFilters, minRating: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Max Rating</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="e.g., 5"
                    min="1"
                    max="5"
                    value={tempFilters.maxRating}
                    onChange={(e) => setTempFilters({...tempFilters, maxRating: e.target.value})}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-outline" 
                onClick={handleCancelFilters}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleApplyFilters}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && viewingApplicant && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth: '900px'}}>
            <div className="modal-header">
              <h3 className="modal-title">Released Applicant Details</h3>
              <button onClick={() => setShowDetailsModal(false)}>×</button>
            </div>
            <div className="modal-body" style={{maxHeight: '70vh', overflowY: 'auto'}}>
              {/* Header */}
              <div style={{marginBottom: '24px', paddingBottom: '16px', borderBottom: '2px solid #e2e8f0'}}>
                <h2 style={{marginBottom: '8px'}}>{viewingApplicant.first_name} {viewingApplicant.last_name}</h2>
                <div style={{display: 'flex', gap: '12px', flexWrap: 'wrap'}}>
                  <span className="badge badge-primary">{viewingApplicant.position_title}</span>
                  <span className="badge badge-secondary">{viewingApplicant.position_location}</span>
                  <span className="badge badge-danger">Released: {new Date(viewingApplicant.released_date).toLocaleDateString()}</span>
                  {viewingApplicant.rating && <span className="badge badge-info">Rating: {viewingApplicant.rating}/5</span>}
                </div>
              </div>

              {/* Contact Information */}
              <div className="review-section">
                <h3>Contact Information</h3>
                <div className="grid grid-2" style={{gap: '12px'}}>
                  <div className="review-item"><strong>Email:</strong> {viewingApplicant.email}</div>
                  <div className="review-item"><strong>Phone:</strong> {viewingApplicant.phone || 'N/A'}</div>
                  <div className="review-item"><strong>Address:</strong> {viewingApplicant.address || 'N/A'}</div>
                  <div className="review-item"><strong>City, State ZIP:</strong> {viewingApplicant.city}, {viewingApplicant.state} {viewingApplicant.zip}</div>
                </div>
              </div>

              {/* Resume / CV */}
              <ApplicantResume
                applicantId={viewingApplicant.id}
                applicantName={viewingApplicant.first_name}
              />

              {/* Notes */}
              <ApplicantNotes
                applicantId={viewingApplicant.id}
                canEdit={user?.role === 'admin' || user?.permissions?.edit_applicants}
              />
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowDetailsModal(false)}>
                Close
              </button>
              <button className="btn btn-primary" onClick={handleExportPDF}>
                📄 Export to PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReleasedList;
